import { useMemo, type ReactNode } from "react";
import { Check } from "lucide-react";
import { cn } from "./ui";
import { t } from "@/lib/i18n";

/* Общие части мастеров подключения каналов: типы, заголовки, шаги, строки итогов. */

export type WizardChannel =
  | "whatsapp"
  | "waba"
  | "telegram"
  | "telegram-bot"
  | "viber"
  | "viber-bot"
  | "instagram"
  | "instagram-facebook"
  | "email";

/** Какой карточке каталога принадлежит тип подключения. */
export const WIZARD_CATALOG_ID: Record<WizardChannel, string> = {
  whatsapp: "whatsapp",
  waba: "whatsapp",
  telegram: "telegram",
  "telegram-bot": "telegram",
  viber: "viber",
  "viber-bot": "viber",
  instagram: "instagram",
  "instagram-facebook": "instagram",
  email: "email",
};
export type WhatsLogin = "qr" | "phone";
export type RowData = { label: string; value: string };
export type PanelProps = { onDone: (account: string, rows: RowData[]) => void };

/** Куда рисовать всплывающее окно.
    Важно: тема сайта (цвета, шрифты, радиусы) живёт в CSS-переменных на .flash-site.
    Если отрисовать окно прямо в document.body, переменных там нет — фон становится прозрачным.
    Поэтому окна монтируем внутрь корня темы. */
export function useThemePortal(): HTMLElement {
  return useMemo(() => {
    return (document.querySelector(".flash-site") as HTMLElement | null) ?? document.body;
  }, []);
}

export const CHANNEL_META: Record<
  WizardChannel,
  { name: string; short: string; icon: string; sub: string; doneText: string }
> = {
  whatsapp: {
    name: "WhatsApp",
    short: "WhatsApp",
    icon: "ri-whatsapp-line",
    sub: t("Обычный аккаунт: вход по QR-коду или по номеру"),
    doneText:
      t("Номер привязан, история переписки начнёт подтягиваться. Новые сообщения уже идут во «Входящие»."),
  },
  waba: {
    name: "WhatsApp Business API",
    short: "WABA",
    icon: "ri-whatsapp-line",
    sub: t("Официальный аккаунт: рассылки и шаблоны"),
    doneText:
      t("Официальный аккаунт подключён. Шаблоны и рассылки доступны, вебхук установлен."),
  },
  telegram: {
    name: "Telegram",
    short: "Telegram",
    icon: "ri-telegram-line",
    sub: t("Аккаунт: вход по QR-коду из Telegram"),
    doneText: t("Аккаунт привязан. Новые переписки сохраняются в CRM, история за последние дни подтягивается."),
  },
  "telegram-bot": {
    name: "Telegram Bot",
    short: t("Бот"),
    icon: "ri-robot-2-line",
    sub: t("Бот по токену от @BotFather"),
    doneText:
      t("Бот запущен и принимает сообщения. Вебхук установлен, доступы сохранены у администратора."),
  },
  viber: {
    name: "Viber",
    short: "Viber",
    icon: "ri-phone-line",
    sub: t("Аккаунт: вход по QR-коду из Viber"),
    doneText:
      t("Аккаунт привязан. Новые переписки сохраняются в CRM, последние диалоги уже подтянулись."),
  },
  "viber-bot": {
    name: "Viber Business",
    short: t("Бизнес"),
    icon: "ri-robot-2-line",
    sub: t("Официальный аккаунт по токену"),
    doneText:
      t("Официальный аккаунт подключён. Рассылки и автоответы доступны, вебхук установлен."),
  },
  instagram: {
    name: "Instagram Direct",
    short: "Instagram",
    icon: "ri-instagram-line",
    sub: t("Аккаунт: вход по логину и коду"),
    doneText:
      t("Аккаунт привязан. Личные сообщения и ответы на сторис идут во «Входящие»."),
  },
  email: {
    name: t("Почта"),
    short: "Gmail",
    icon: "ri-mail-line",
    sub: t("Ящик сотрудника в Google Workspace: по адресу, без паролей"),
    doneText: t("Ящик подключён. Переписка с клиентами за прошлый год подтягивается в фоне, новые письма появляются во «Входящих» каждые несколько минут."),
  },
  "instagram-facebook": {
    name: t("Instagram через Facebook"),
    short: t("Через Facebook"),
    icon: "ri-facebook-circle-line",
    sub: t("Бизнес-страница через Meta Business Suite"),
    doneText:
      t("Страница подключена. Instagram Direct и Messenger работают в общей ленте диалогов."),
  },
};

export function Row({ label, value }: RowData) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/60 py-2 last:border-0">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="min-w-0 truncate text-right text-[12.5px] font-semibold">{value}</span>
    </div>
  );
}

export function Steps({
  items,
  active,
  done,
}: {
  items: string[];
  active: number;
  done?: boolean;
}) {
  return (
    <ol className="flex flex-col gap-3">
      {items.map((s, i) => {
        const n = i + 1;
        const isActive = !done && n === active;
        const isDone = done || n < active;
        return (
          <li key={s} className="flex items-start gap-2.5">
            <span
              className={cn(
                "mt-0.5 grid size-6 flex-none place-items-center rounded-full text-[11px] font-bold tabular-nums transition",
                isDone
                  ? "bg-emerald-500 text-white"
                  : isActive
                    ? "bg-primary/15 text-primary ring-1 ring-primary/40"
                    : "bg-muted text-muted-foreground",
              )}
              aria-hidden
            >
              {isDone ? <Check className="size-3" strokeWidth={3} /> : n}
            </span>
            <p
              className={cn(
                "text-[12.5px] leading-relaxed",
                isActive ? "font-medium text-foreground" : "text-muted-foreground",
              )}
            >
              {s}
            </p>
          </li>
        );
      })}
    </ol>
  );
}

export function Hint({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
      <span className="mt-0.5 flex-none text-primary" aria-hidden>
        {icon}
      </span>
      <span>{children}</span>
    </p>
  );
}

export function SuccessBox({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="mt-3 rounded-btn border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5">
      <p className="flex items-center gap-1.5 text-[12px] font-semibold text-emerald-700">
        <Check className="size-3.5" strokeWidth={3} aria-hidden />
        {title}
      </p>
      {children ? <div className="mt-2">{children}</div> : null}
    </div>
  );
}

export function SideCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-card border border-border bg-background p-3">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{title}</p>
      {children}
    </div>
  );
}

export const inputCls =
  "h-10 w-full min-w-0 box-border rounded-btn border border-border bg-background px-3 text-[12.5px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

export const monoInputCls = `${inputCls} font-mono text-[12px]`;